import React, { useState, useMemo, useEffect } from "react";
import { useTable, useSortBy, usePagination } from "react-table";
import { listInitialStateDefaults } from "../../helpers/constants";
import { actions, downloadFileMessages } from "../../helpers/messages";
import { getFileIcon, toTitleCase } from "../../helpers/GlobalFunctions";
import ErrorMsgBlockComponent from "../Error/ErrorMessageBlock";
import CustomModal from "../../shared/CustomModal";
import Download from "./Download";
import { convertUTCToLocalDateTime } from "../../helpers/GlobalFunctions";

const DownloadButton = ({ item, handleDownload }) => {
	return (
		<button className="btn btn-outline-primary download-button" title={`Download ${item.name}`} onClick={(e) => handleDownload(item)}>
			<span className="d-flex align-items-center">
				<svg width="16px" height="16px" fill="currentColor" className="bi bi-download" viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg">
					<path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5z" />
					<path d="M7.646 11.854a.5.5 0 0 0 .708 0l3-3a.5.5 0 0 0-.708-.708L8.5 10.293V1.5a.5.5 0 0 0-1 0v8.793L5.354 8.146a.5.5 0 1 0-.708.708l3 3z" />
				</svg>
				<span className="ms-2">Download</span>
			</span>
		</button>
	)
}

const Breadcrumb = ({ path, onNavigate, handleDownload, searched, uploadOnClick }) => {
	return (<>
		<div className="d-flex align-items-center justify-content-between">
			<nav aria-label="breadcrumb" className="breadcrumb-wrap">
				<ol className="breadcrumb mb-0">
					{path.map((item) => (
						<li key={item.id} className="breadcrumb-item">
							<button onClick={() => onNavigate(item)} className="btn btn-link p-0">{item.name}</button>
						</li>
					))}
				</ol>
			</nav>
			<div className="button-group text-end">
				{path?.length > 1 ? <DownloadButton item={path[1]} handleDownload={handleDownload} /> : ''}
				{searched && path?.length ? <>
					<button type="button" className="btn btn-primary ms-2" id="upload-log-file" data-testid="upload-log-file" onClick={uploadOnClick}>
						<span className="d-flex align-items-center">
							<svg xmlns="http://www.w3.org/2000/svg" width="16px" height="16px" fill="currentColor" className="bi bi-upload" viewBox="0 0 16 16">
								<path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5" />
								<path d="M7.646 1.146a.5.5 0 0 1 .708 0l3 3a.5.5 0 0 1-.708.708L8.5 2.707V11.5a.5.5 0 0 1-1 0V2.707L5.354 4.854a.5.5 0 1 1-.708-.708z" />
							</svg>
							<span className="ms-2">Upload</span>
						</span>
					</button>
				</> : ''}
			</div>
		</div>
	</>
	);
};

const RecursiveList = (props) => {
	const [currentData, setCurrentData] = useState([]);
	const [source, setSource] = useState(null);
	const [totalCount, setTotalCount] = useState(0);
	const [downloadFileObj, setDownloadFileObj] = useState(null);
	const [showDownloadModal, setShowDownloadModal] = useState(false);
	const defaultBreadcrumbPath = { id: -1, name: "All", children: props.caseList }
	const [breadcrumbPath, setBreadcrumbPath] = useState([defaultBreadcrumbPath]);

	const getIcon = (item) => {
		if (item.type === 'folder') {
			return (
				<svg xmlns="http://www.w3.org/2000/svg" width="1.8em" height="1.8em" fill="currentColor" className="bi bi-folder2" viewBox="0 0 16 16">
					<path d="M1 3.5A1.5 1.5 0 0 1 2.5 2h2.764c.958 0 1.76.56 2.311 1.184C7.985 3.648 8.48 4 9 4h4.5A1.5 1.5 0 0 1 15 5.5v7a1.5 1.5 0 0 1-1.5 1.5h-11A1.5 1.5 0 0 1 1 12.5zM2.5 3a.5.5 0 0 0-.5.5V6h12v-.5a.5.5 0 0 0-.5-.5H9c-.964 0-1.71-.629-2.174-1.154C6.374 3.334 5.82 3 5.264 3zM14 7H2v5.5a.5.5 0 0 0 .5.5h11a.5.5 0 0 0 .5-.5z" />
				</svg>
			)
		}
		return getFileIcon(item.name)
	}

	const columns = useMemo(
		() => [
			{
				Header: "Name",
				accessor: "name",
				Cell: ({ row }) => (
					<div className="name-column">
						<div className="icon-wrap d-inline me-2">{getIcon(row.original)}</div>
						<span className={row.original.type === 'folder' ? "btn btn-link m-0 p-0" : "filename"} onClick={(e) => handleRowClick(row.original)} title={row?.original?.type === 'folder' ? `View Subfolders` : ''}>
							<span>{row?.original?.name}</span>
						</span>
					</div>
				),
			},
			{
				Header: 'Source',
				accessor: 'source',
				Cell: ({ row }) => (<span>{row?.original?.source?.toUpperCase() || source?.toUpperCase() || 'N/A'}</span>),
				Filter: ''
			},
			{
				Header: 'Type',
				accessor: 'type',
				Cell: ({ row }) => (<span>{toTitleCase(row?.original?.type)}</span>),
				Filter: ''
			},
			{
				Header: 'Uploaded Date',
				accessor: 'lastModified',
				Cell: ({ row }) => (<span>{row?.original?.lastModified ? convertUTCToLocalDateTime(row?.original?.lastModified) : "N/A"}</span>),
				Filter: ''
			},
			{
				Header: ' ',
				Cell: ({ row }) => (
					row.original.showDownload ?
						<div className="sys-log-actions text-end">
							<DownloadButton item={row.original} handleDownload={handleDownloadClick} />
						</div >
						: ''
				)
			},
		],
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[source]
	);

	useEffect(() => {
		if (props.caseList && Array.isArray(props.caseList)) {
			setCurrentData(props.caseList)
			setTotalCount(props.caseList?.length)
			setBreadcrumbPath([{ id: -1, name: "All", children: [] }])
		}
	}, [props.caseList])

	// close download model once file download is success
	useEffect(() => {
		if (props.downloadSuccess) {
			setTimeout(() => {
				setShowDownloadModal(false)
			}, 6000)
		}
	}, [props.downloadSuccess])

	const {
		getTableProps,
		getTableBodyProps,
		headerGroups,
		page,
		prepareRow,
		pageOptions,
		canPreviousPage,
		canNextPage,
		nextPage,
		previousPage,
		gotoPage,
		setPageSize,
		pageCount,
		state: { pageIndex, pageSize }
	} = useTable(
		{
			columns,
			data: currentData,
			initialState: listInitialStateDefaults,
		},

		useSortBy,
		usePagination
	);

	const handleBreadcrumbClick = (item) => {
		if (item.id === -1) {
			setCurrentData(props.caseList);
			setTotalCount(props.caseList?.length);
			setBreadcrumbPath([defaultBreadcrumbPath]);
		} else {
			const newPath = breadcrumbPath.slice(0, breadcrumbPath.findIndex((p) => p.id === item.id) + 1);
			setCurrentData(item.children);
			setTotalCount(item.children?.length);
			setBreadcrumbPath(newPath);
		}
	};

	const handleRowClick = (item) => {
		if (item?.type === 'folder') {
			if (item?.source) {
				setSource(item.source);
			}
			setBreadcrumbPath(path => [
				...path,
				item
			]);
			setCurrentData(item?.children?.length ? item.children : []);
			setTotalCount(item?.children?.length);
		}
	};

	const handleDownloadClick = (item) => {
		setDownloadFileObj(item)
		setShowDownloadModal(true)
	}

	const handleDownloadCancel = () => {
		setDownloadFileObj(null)
		setShowDownloadModal(false)
	}


	return (<div>
		{props.downloadSuccess && (
			<div className="text-success mb-1 text-aign-center">
				<span>{downloadFileMessages.success}</span>
			</div>
		)}
		{props.downloadError && <ErrorMsgBlockComponent errorObject={props.downloadError} />}

		<Breadcrumb path={breadcrumbPath} onNavigate={handleBreadcrumbClick} handleDownload={handleDownloadClick} searched={props.searched} uploadOnClick={props.uploadOnClick} />

		<div className="table-responsive">
			<div className="case-list-table table-contents file-list">
				<table {...getTableProps()}>
					<thead>
						{headerGroups.map((headerGroup) => {
							let { key, ...restHeaderGroupProps } = headerGroup.getHeaderGroupProps()
							return (
								<tr key={key} {...restHeaderGroupProps}>
									{headerGroup.headers.map((column) => {
										let { key, ...restColumn } = column.getHeaderProps(column.getSortByToggleProps())
										// Add the sorting props to control sorting. For this example
										// we can add them into the header props
										return (
											<th key={key} {...restColumn} className="align-start" title="">
												{column.render("Header")}
												{/* Add a sort direction indicator */}
												<span>
												   {column.isSorted ? (
													    column.isSortedDesc ? (
														   <svg className="bi bi-chevron-down" width="1.2em" height="1.2em" viewBox="0 0 16 16" fill="currentColor" xmlns="http://www.w3.org/2000/svg" >
																<path fillRule="evenodd" d="M1.646 4.646a.5.5 0 01.708 0L8 10.293l5.646-5.647a.5.5 0 01.708.708l-6 6a.5.5 0 01-.708 0l-6-6a.5.5 0 010-.708z" clipRule="evenodd" />
													   		</svg>
														) : (
															<svg className="bi bi-chevron-up" width="1.2em" height="1.2em" viewBox="0 0 16 16" fill="currentColor" xmlns="http://www.w3.org/2000/svg" >
														   		<path fillRule="evenodd" d="M7.646 4.646a.5.5 0 01.708 0l6 6a.5.5 0 01-.708.708L8 5.707l-5.646 5.647a.5.5 0 01-.708-.708l6-6z" clipRule="evenodd" />
															</svg>)
														) : (
													column.id === "lastModified" ? (
														   <svg className="bi bi-chevron-down" width="1.2em" height="1.2em" viewBox="0 0 16 16" fill="currentColor" xmlns="http://www.w3.org/2000/svg" >
																<path fillRule="evenodd" d="M1.646 4.646a.5.5 0 01.708 0L8 10.293l5.646-5.647a.5.5 0 01.708.708l-6 6a.5.5 0 01-.708 0l-6-6a.5.5 0 010-.708z" clipRule="evenodd" />
													   		</svg>
														) : '')}
												</span>
												<div>
													{column.canFilter ? column.render("Filter") : null}
												</div>
											</th>)
									})}
								</tr>)
						}
						)}
					</thead>
					{props.loading ? (
						<tbody className="rt-tbody tr-refresh">
							{[...Array(page && page.length && page.length < pageSize ? page.length : 10)].map((e, i) => (
								<tr key={i}>
									{[...Array(headerGroups.length ? headerGroups[0].headers.length : 1)].map((f, j) => (
										<td className="loading-cell" key={j}>
											<span></span>
										</td>
									))}
								</tr>
							))}
							<tr>
								<td colSpan="10000">{actions.load}</td>
							</tr>
						</tbody>
					) : (
						<tbody {...getTableBodyProps()} className="rt-tbody">
							{page.map((row, i) => {
								prepareRow(row);
								let { key, ...restRowProps } = row.getRowProps();
								return (
									<React.Fragment key={i}>
										<tr key={key} {...restRowProps}>
											{row.cells.map((cell) => {
												let { key, ...restColProps } = cell.getCellProps();
												return (
													<td key={key} {...restColProps} className="align-start">{cell.render("Cell")}</td>
												);
											})}
										</tr>
									</React.Fragment>
								);
							})}
							{page.length === 0 && (
								<tr className="text-center py-3">
									<td colSpan="10000">
										<ErrorMsgBlockComponent className="py-4 m-0" noData={true} />
									</td>
								</tr>
							)}

							{page.length && !props.loading ? <tr className="result-count"><td colSpan="10000">Showing {page.length} of {totalCount} results</td></tr> : ''}
						</tbody>
					)}
				</table>
				{totalCount > 0 && props.showPagination ?
					<div className="pagination">
						<button className="me-1" onClick={() => gotoPage(0)} disabled={!canPreviousPage}>{"<<"}</button>
						<button className="me-1" onClick={() => previousPage()} disabled={!canPreviousPage}>{"<"}</button>
						<button className="me-1" onClick={() => nextPage()} disabled={!canNextPage}>{">"}</button>
						<button className="me-1" onClick={() => gotoPage(pageCount - 1)} disabled={!canNextPage}>{">>"}</button>
						<span className="page-display me-1">Page <strong>{pageIndex >= pageOptions.length ? pageOptions.length : pageIndex + 1} of {pageOptions.length}</strong></span>

						<select className="form-select res-dropdown" value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); }}>
							{[10, 20, 30, 40, 50, 100].map((pageSize) => (
								<option key={pageSize} value={pageSize}>Show {pageSize}</option>
							))}
						</select>
					</div>
					: ''}
			</div>
		</div>

		{/* User consent and download confirmation modal */}
		<CustomModal
			id="download-consent-modal"
			show={showDownloadModal}
			title="Confirm Download"
			isComponent={true}
			dialogClassName="download-consent-dialog"
			hideClose={props.isFilePreparing}
			closeAction={() => handleDownloadCancel()}>
			<Download downloadFileObj={downloadFileObj} searchParams={props.searchParams} handleDownload={props.handleDownload}
				handleDownloadCancel={handleDownloadCancel}
				isFilePreparing={props.isFilePreparing} downloadSuccess={props.downloadSuccess} downloadError={props.downloadError} />
		</CustomModal>
	</div>);
};

export default RecursiveList;
